"use client";

import { useEffect } from "react";
import { useSearchParams, useRouter, usePathname } from "next/navigation";
import dynamic from "next/dynamic";
import { useGenerationStore } from "@/store/generation-store";

const KlingMotionView = dynamic(
    () => import("@/components/kling-motion/view").then((m) => ({ default: m.KlingMotionView })),
    { ssr: false },
);

/**
 * Reads `?motionVideo=<url>` (or legacy `?preset=<id>`) from the URL
 * — e.g. when a Kling Motion preset trend on MobileHome routes here —
 * and feeds the motion-video URL into the generation store's
 * `pendingPresetRef`. KlingMotionView consumes that on mount and uses
 * it as the `<video src>` of the motion reference, which is what makes
 * the preset look «autoselected».
 *
 * URL is preferred over id because the view's downstream code expects
 * a fetchable media URL (preset id alone rendered as «Custom Video»).
 */
export function KlingMotionPageClient() {
    const searchParams = useSearchParams();
    const router = useRouter();
    const pathname = usePathname();
    const setPendingPresetRef = useGenerationStore((s) => s.setPendingPresetRef);

    useEffect(() => {
        const motionVideoUrl = searchParams.get("motionVideo");
        const presetId = searchParams.get("preset");
        const ref = motionVideoUrl || presetId;
        if (!ref) return;
        setPendingPresetRef(ref);
        const params = new URLSearchParams(searchParams.toString());
        params.delete("motionVideo");
        params.delete("preset");
        const qs = params.toString();
        router.replace(qs ? `${pathname}?${qs}` : pathname, { scroll: false });
    }, [searchParams, setPendingPresetRef, router, pathname]);

    return <KlingMotionView />;
}
